// src/oura/delivery/drawerParts.tsx
//
// The pieces inside a deliverable's drawer: an item with its square, the
// pages inside an opened item, and the small forms a staff action needs (a
// tick's link or note, a live page, a proof for closing by hand, a reason).

import { useState, type FormEvent, type ReactNode } from "react";
import { ChevronDownIcon } from "lucide-react";
import type { DeliveryItem, ItemPage } from "@service/aeo/delivery";
import { KIND_LABEL } from "@service/aeo/delivery";
import { cn } from "../lib/cn";
import { PillButton, Tag, TYPE } from "../ui";
import { useWritesPaused } from "@context/maintenance";
import { KIND_TONE, itemFact, squareOf } from "./format";
import { Square } from "./Square";

export const FIELD = cn(
    TYPE.tb,
    "block w-full rounded-xl border-0 bg-gray-50 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600",
);

const PAGE_WORD: Record<ItemPage["state"], string> = {
    todo: "To do",
    waiting: "Marked fixed",
    done: "Fixed",
    not_read: "Not read yet",
    closed_by_hand: "Closed by hand",
    no_action: "No action",
};

const PAGE_SQUARE: Record<ItemPage["state"], "todo" | "waiting" | "done"> = {
    todo: "todo",
    waiting: "waiting",
    done: "done",
    not_read: "waiting",
    closed_by_hand: "done",
    no_action: "done",
};

/**
 * What a page row says. A page Oura has since read clear keeps the backend's
 * old failure text ("Returned 404") in `detail`; beside a green tick that read
 * as still broken (30 Oct 2026), so a fixed page says so and keeps the old
 * failure only as what it was.
 */
export function pageWord(p: Pick<ItemPage, "state" | "detail">): string {
    if (p.state === "done") {
        // Only the backend's kept failure ("Returned 404") is rewritten; any other detail stands as it is.
        const detail = p.detail?.trim();
        const returned = detail?.match(/^returned\s+(.+)$/i);
        if (returned) return `Fixed on the re-check (was ${returned[1]})`;
        return detail || PAGE_WORD.done;
    }
    return p.detail ?? PAGE_WORD[p.state];
}

/** The pages inside an opened item: each with its state, and No action for a page someone decides to leave. */
export function PagesList({
    item,
    busy,
    onNoAction,
}: {
    item: DeliveryItem;
    busy: boolean;
    onNoAction?: (page: string) => void;
}) {
    const writesPaused = useWritesPaused();
    if (item.pages.length === 0) return <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{item.kind === "check" ? "No page fails this check." : "No page yet."}</p>;
    return (
        <ul className="m-0 list-plain space-y-1 p-0">
            {item.pages.map((p) => {
                const sq = p.cameBack && p.state === "todo" ? "came_back" : PAGE_SQUARE[p.state];
                return (
                    <li key={p.url} className={cn(TYPE.tc, "flex items-center gap-2")}>
                        <Square state={sq} />
                        <span className="min-w-0 flex-1 truncate text-gray-900" title={p.url}>
                            {p.url}
                        </span>
                        <span className={cn("shrink-0", p.cameBack && p.state === "todo" ? "text-rose-600" : "text-gray-500")}>
                            {p.cameBack && p.state === "todo" ? "Came back" : pageWord(p)}
                            {p.noActionReason ? `: ${p.noActionReason}` : ""}
                        </span>
                        {onNoAction && p.state === "todo" && item.kind === "check" && (
                            <button
                                type="button"
                                disabled={busy || writesPaused}
                                onClick={() => onNoAction(p.url)}
                                className={cn(TYPE.tm, "shrink-0 rounded-full px-2 py-0.5 font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40")}
                            >
                                No action
                            </button>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}

/** One item: its square, name and one fact; opened, its kind, what staff do, its pages and its actions. */
export function ItemRow({
    item,
    isNext,
    open,
    onToggle,
    children,
}: {
    item: DeliveryItem;
    isNext: boolean;
    open: boolean;
    onToggle: () => void;
    children?: ReactNode;
}) {
    const sq = squareOf(item);
    return (
        <div className="border-t border-gray-100 first:border-t-0" data-item={item.id}>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                className="group flex w-full items-start gap-2.5 py-2.5 text-left"
            >
                <Square state={sq} className="mt-[3px]" />
                <span className="min-w-0 flex-1">
                    <span className={cn(TYPE.tb, "block text-gray-900 group-hover:text-blue-600")}>
                        {item.label}
                        {item.outlet && item.kind === "confirm" ? <span className="text-gray-500"> · {item.outlet}</span> : null}
                    </span>
                    <span className={cn(TYPE.tc, "block", sq === "came_back" ? "text-rose-600" : "text-gray-500")}>{itemFact(item)}</span>
                </span>
                {isNext && <span className={cn(TYPE.tm, "mt-0.5 shrink-0 rounded-full bg-blue-50 px-2 py-0.5 font-medium text-blue-700")}>next</span>}
                <ChevronDownIcon aria-hidden size={16} className={cn("mt-0.5 shrink-0 text-gray-400 transition-transform", open && "rotate-180")} />
            </button>
            {open && (
                <div className="space-y-3 pb-3 pl-6">
                    <div className="flex flex-wrap items-center gap-1.5">
                        <Tag tone={KIND_TONE[item.kind]}>{KIND_LABEL[item.kind]}</Tag>
                        {item.scope === "key_pages" && <Tag tone="gray">Key pages</Tag>}
                        {item.scope === "whole_site" && <Tag tone="gray">Whole site</Tag>}
                        {item.slot?.signature && <Tag tone="teal">Signature</Tag>}
                        {item.slot?.pageKind && <Tag tone="gray">{item.slot.pageKind === "new" ? "New page" : "Existing page"}</Tag>}
                        {item.technical && <span className={cn(TYPE.tm, "text-gray-400")}>{item.technical}</span>}
                    </div>
                    {item.staffDo && item.state === "todo" && <p className={cn(TYPE.tc, "m-0 text-gray-600")}>{item.staffDo}.</p>}
                    {item.slot?.changed && <p className={cn(TYPE.tc, "m-0 text-gray-600")}>What changed: {item.slot.changed}</p>}
                    {item.proof && (
                        <p className={cn(TYPE.tc, "m-0 text-gray-600")}>
                            Proof: {item.proof.text}
                            {item.proof.by ? `, ${item.proof.by}` : ""}
                            {item.proofFile ? ` · ${item.proofFile.name}` : ""}
                            {item.proof.url ? (
                                <>
                                    {" · "}
                                    <a href={item.proof.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                                        Open the link
                                    </a>
                                </>
                            ) : null}
                        </p>
                    )}
                    {children}
                </div>
            )}
        </div>
    );
}

/** A section of items under a small heading: "Also to do 1", "Waiting on Oura's check 2". */
export function ItemSection({ title, count, right, children }: { title: string; count: number; right?: ReactNode; children: ReactNode }) {
    return (
        <section className="rounded-2xl bg-white px-4 py-3 ring-1 ring-black/[0.04]">
            <div className="flex items-center gap-2">
                <h3 className={cn(TYPE.lab, "m-0")}>{title}</h3>
                <span className={cn(TYPE.tc, "tabular-nums text-gray-400")}>{count}</span>
                {right && <span className="ml-auto">{right}</span>}
            </div>
            <div className="mt-1">{children}</div>
        </section>
    );
}

/* ------------------------------------------------------------------------- *
 * Forms
 * ------------------------------------------------------------------------- */

function FormShell({ title, busy, error, submit, onCancel, onSubmit, children }: {
    title: string;
    busy: boolean;
    error: ReactNode;
    submit: string;
    onCancel: () => void;
    onSubmit: () => void;
    children: ReactNode;
}) {
    const go = (e: FormEvent) => {
        e.preventDefault();
        onSubmit();
    };
    return (
        <form onSubmit={go} className="space-y-3 rounded-2xl bg-white p-4 ring-2 ring-blue-600/30" aria-label={title}>
            <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>{title}</p>
            {children}
            {error && (
                <p role="alert" className={cn(TYPE.tc, "m-0 text-rose-600")}>
                    {error}
                </p>
            )}
            <div className="flex items-center gap-2">
                <PillButton type="submit" variant="primary" busy={busy} writes>
                    {submit}
                </PillButton>
                <PillButton onClick={onCancel} disabled={busy}>
                    Cancel
                </PillButton>
            </div>
        </form>
    );
}

export function TickForm({ label, busy, error, onCancel, onSubmit }: {
    label: string;
    busy: boolean;
    error: ReactNode;
    onCancel: () => void;
    onSubmit: (body: { link?: string; note?: string }) => void;
}) {
    const [link, setLink] = useState("");
    const [note, setNote] = useState("");
    return (
        <FormShell title={`Tick ${label.toLowerCase()}`} busy={busy} error={error} submit="Tick it" onCancel={onCancel} onSubmit={() => onSubmit({ link: link.trim() || undefined, note: note.trim() || undefined })}>
            <label className="block">
                <span className={TYPE.lab}>A link</span>
                <input className={cn(FIELD, "mt-1")} value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://" inputMode="url" autoFocus />
            </label>
            <label className="block">
                <span className={TYPE.lab}>Or a one-line note</span>
                <input className={cn(FIELD, "mt-1")} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What proves it is done" maxLength={200} />
            </label>
        </FormShell>
    );
}

export function PageForm({ item, busy, error, onCancel, onSubmit }: {
    item: DeliveryItem;
    busy: boolean;
    error: ReactNode;
    onCancel: () => void;
    onSubmit: (body: { pageKind: "new" | "existing"; url: string; changed?: string }) => void;
}) {
    const [kind, setKind] = useState<"new" | "existing">(item.slot?.pageKind ?? "new");
    const [url, setUrl] = useState(item.pages[0]?.url ?? "");
    const [changed, setChanged] = useState("");
    const topic = item.slot?.topic ? `: ${item.slot.topic}` : "";
    return (
        <FormShell
            title={`Add a live page${topic}`}
            busy={busy}
            error={error}
            submit="Add the page"
            onCancel={onCancel}
            onSubmit={() => onSubmit({ pageKind: kind, url: url.trim(), changed: kind === "existing" ? changed.trim() : undefined })}
        >
            <fieldset className="m-0 flex flex-wrap gap-2 border-0 p-0">
                <legend className={cn(TYPE.lab, "mb-1")}>Agreed with the client</legend>
                {(["new", "existing"] as const).map((k) => (
                    <label
                        key={k}
                        className={cn(
                            TYPE.tc,
                            "flex cursor-pointer items-center gap-2 rounded-full px-3 py-1.5 ring-1 ring-inset",
                            kind === k ? "bg-blue-50 text-blue-700 ring-blue-200" : "bg-white text-gray-600 ring-gray-200",
                        )}
                    >
                        <input type="radio" name="page-kind" value={k} checked={kind === k} onChange={() => setKind(k)} className="sr-only" />
                        {k === "new" ? "A new page" : "An existing page improved"}
                    </label>
                ))}
            </fieldset>
            <label className="block">
                <span className={TYPE.lab}>The live address</span>
                <input className={cn(FIELD, "mt-1")} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="/nyonya-catering or https://" autoFocus />
            </label>
            {kind === "existing" && (
                <label className="block">
                    <span className={TYPE.lab}>What changed, in one line</span>
                    <input className={cn(FIELD, "mt-1")} value={changed} onChange={(e) => setChanged(e.target.value)} placeholder="Rewritten to answer the catering questions" maxLength={200} />
                </label>
            )}
            <p className={cn(TYPE.tc, "m-0 text-gray-500")}>Oura reads the page on its next check. Oura cannot create a page.</p>
        </FormShell>
    );
}

export function ProofForm({ title, submit, hint, withFile = false, busy, error, onCancel, onSubmit }: {
    title: string;
    submit: string;
    hint?: string;
    /** H36 (v1.3): a screenshot or a PDF goes with the proof. Only behind the Phase 3 switches. */
    withFile?: boolean;
    busy: boolean;
    error: ReactNode;
    onCancel: () => void;
    onSubmit: (body: { link?: string; note: string; file?: File | null }) => void;
}) {
    const [note, setNote] = useState("");
    const [link, setLink] = useState("");
    const [file, setFile] = useState<File | null>(null);
    return (
        <FormShell
            title={title}
            busy={busy}
            error={error}
            submit={submit}
            onCancel={onCancel}
            onSubmit={() => onSubmit({ note: note.trim(), link: link.trim() || undefined, ...(withFile ? { file } : {}) })}
        >
            <label className="block">
                <span className={TYPE.lab}>How you know it is done</span>
                <input className={cn(FIELD, "mt-1")} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Checked on a phone: the markup is there" maxLength={200} autoFocus />
            </label>
            <label className="block">
                <span className={TYPE.lab}>A link, if there is one</span>
                <input className={cn(FIELD, "mt-1")} value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://" inputMode="url" />
            </label>
            {withFile && (
                <label className="block">
                    <span className={TYPE.lab}>A screenshot or a PDF, if you have one</span>
                    <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                        className={cn(
                            TYPE.tc,
                            "mt-1 block w-full text-gray-600 file:mr-3 file:rounded-full file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:font-medium file:text-gray-900 hover:file:bg-gray-200",
                        )}
                    />
                </label>
            )}
            {hint && <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{hint}</p>}
        </FormShell>
    );
}

export function ReasonForm({ title, submit, placeholder, busy, error, onCancel, onSubmit }: {
    title: string;
    submit: string;
    placeholder: string;
    busy: boolean;
    error: ReactNode;
    onCancel: () => void;
    onSubmit: (reason: string) => void;
}) {
    const [reason, setReason] = useState("");
    return (
        <FormShell title={title} busy={busy} error={error} submit={submit} onCancel={onCancel} onSubmit={() => onSubmit(reason.trim())}>
            <label className="block">
                <span className={TYPE.lab}>Why, in one line</span>
                <input className={cn(FIELD, "mt-1")} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={placeholder} maxLength={200} autoFocus />
            </label>
        </FormShell>
    );
}
